import { IconButton, Stack, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/system';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { AddIcon, RemoveIcon } from '@/assets/icons';
import GridIcon from '@/assets/icons/GridIcon';
import { AppServiceFactory } from '@/modules/app/service/AppServiceFactory';
import { IsLatestRevisionSelector } from '@/selectors';
import { useConfirm } from '@/modules/common/components/useConfirm';
import { StyledTooltip } from '@modules/common/components/StyledTooltip';
import { OverflowTypography } from '@/modules/common/components/OverflowTypography';
import { setupPropertyPanelTheme, theme } from '@/modules/common/theme';
import { templatesToFlatCollection } from '@/modules/floorplanDesign/helpers/helpers';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { SnackbarServiceFactory } from '@/modules/snackbar/service/SnackbarServiceFactory';
import { useVehicleStore } from '@/modules/vehicles/store/useVehicleStore';
import { VehicleServiceFactory } from '@/modules/vehicles/service/VehicleServiceFactory';

import {
  getNonShuttleIds,
  isShuttleVehicleId,
} from '../../helpers/shuttleSelectionHelpers';
import { useVehicleRemovalGuard } from '../../hooks/useVehicleRemovalGuard';
import { useDoesVehiclesHaveLoadTypeCheck } from '../../hooks/useDoesAllVehiclesHaveLoadTypeCheck';
import { AssetPanel } from './AssetPanel';
import { VehicleTypeSelectDialog } from './VehicleTypeSelectDialog';

type Props = {
  name: string;
  vehicleId: string;
  imageUrl: string;
  deleteBtnTooltipText: string;
  onDeleteClick: (id: string) => void;
  deletable: boolean;
  disabled?: boolean;
  onFileInteractionClick: (id: string) => void;
  assetsVehicleId: string;
};

const containerSx = {
  height: '48px',
  alignItems: 'center',
  flexDirection: 'row',
  justifyContent: 'space-between',
  padding: 0,
  border: '1px solid transparent',
};

const containerInteractiveSx = {
  '&:hover': {
    borderColor: 'divider',
  },
};

const advancedSettingsIconBtnStyle = {
  border: 0,
  height: '10px',
  p: '0px',
  '&:hover': {
    color: theme.palette.neutral.darker,
  },
  '&:active:hover': {
    backgroundColor: theme.palette.shades.light,
    color: theme.palette.neutral.darker,
    borderColor: 'transparent',
  },
};

const FloorPlanEnabledVehicleItemComponent = ({
  name,
  vehicleId,
  imageUrl,
  onDeleteClick,
  deleteBtnTooltipText,
  deletable,
  disabled = false,
  onFileInteractionClick,
  assetsVehicleId,
}: Props) => {
  const iconButtonColor =
    vehicleId === assetsVehicleId ? theme.palette.primary.main : theme.palette.shades.main;

  const iconButtonClassName = vehicleId === assetsVehicleId ? 'active btn-icon' : 'btn-icon';

  const handleDeleteClick = useCallback(() => onDeleteClick(vehicleId), [onDeleteClick, vehicleId]);

  const handleFileInteractionClick = useCallback(
    () => onFileInteractionClick(vehicleId),
    [onFileInteractionClick, vehicleId],
  );

  return (
    <Stack sx={{ ...containerSx, ...(deletable ? containerInteractiveSx : {}) }}>
      <Stack direction='row' alignItems='center' maxWidth='80%' gap={1}>
        <img src={imageUrl} alt={name} width={32} height={32} />
        <OverflowTypography
          sx={{
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          {name}
        </OverflowTypography>
      </Stack>

      <Stack direction='row' alignItems='center' gap={1}>
        <StyledTooltip
          placement='bottom'
          title={
            <Trans
              i18nKey='setup.floorPlan.vehicle_types.asset_settings_button.title'
              ns='interface'
              components={{
                s: <span />,
              }}
            />
          }
        >
          <IconButton
            color={vehicleId === assetsVehicleId ? 'primary' : 'default'}
            onClick={handleFileInteractionClick}
            className={iconButtonClassName}
            sx={{
              ...advancedSettingsIconBtnStyle,
              color: iconButtonColor,
            }}
          >
            <GridIcon />
          </IconButton>
        </StyledTooltip>

        {deletable && (
          <StyledTooltip title={deleteBtnTooltipText}>
            <span>
              <IconButton
                sx={{ marginLeft: 'auto', padding: 0, border: 'none' }}
                onClick={handleDeleteClick}
                aria-label='delete property item'
                disabled={disabled}
              >
                <RemoveIcon />
              </IconButton>
            </span>
          </StyledTooltip>
        )}
      </Stack>
    </Stack>
  );
};

export const FloorPlanEnabledVehicleItem = memo(FloorPlanEnabledVehicleItemComponent);

const VehicleTypesComponent = () => {
  const { t } = useTranslation(['interface']);

  const [open, setOpen] = useState(false);

  const [assetsVehicleId, setAssetsVehicleId] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);
  const isLatest = IsLatestRevisionSelector.useReactive();
  const { getVehicleTypeById, vehicleTypesInStore, loadTypesInStore } = useFloorplanDesignStore(
    useShallow((state) => ({
      getVehicleTypeById: state.getVehicleTypeById,
      vehicleTypesInStore: state.setup.vehicleTypes,
      loadTypesInStore: state.setup.loadTypes,
    })),
  );
  const { doesVehiclesHaveLoadType } = useDoesVehiclesHaveLoadTypeCheck();
  const { Dialog: ConfirmDialog, confirm } = useConfirm();
  const { canRemoveVehicle } = useVehicleRemovalGuard(confirm);

  const snackbarSvc = useMemo(() => SnackbarServiceFactory.getInstance().createSnackbarService(), []);
  const appSvc = useMemo(() => AppServiceFactory.getInstance().createAppService(), []);
  const vehicleSvc = useMemo(() => VehicleServiceFactory.getInstance().createVehicleService(), []);

  const allVehicles = useVehicleStore((state) => state.allVehicles);

  const enabledVehicleIds = useMemo(
    () => vehicleTypesInStore.map(({ id }) => id),
    [vehicleTypesInStore],
  );
  const enabledRegularVehicleCount = useMemo(
    () => getNonShuttleIds(enabledVehicleIds).length,
    [enabledVehicleIds],
  );
  const enabledVehicles = useMemo(
    () => allVehicles.getAll().filter(({ id }) => enabledVehicleIds.includes(id)),
    [allVehicles, enabledVehicleIds],
  );

  const handleCloseDialog = useCallback(() => setOpen(false), []);
  const handleOpenDialog = useCallback(() => setOpen(true), []);
  const handleCloseAssetPanel = useCallback(() => setAssetsVehicleId(''), []);

  const isVehicleDeletable = useCallback(
    (id: string): boolean => {
      if (isShuttleVehicleId(id)) {
        return enabledVehicleIds.length > 1;
      }

      return enabledRegularVehicleCount > 1;
    },
    [enabledRegularVehicleCount, enabledVehicleIds.length],
  );

  const onSelect = useCallback(
    async (newEnabledIds: string[]) => {
      setOpen(false);

      const {
        templates,
        areas: { highwayAreas },
      } = useFloorplanDesignStore.getState().design;

      const templateGroups = templatesToFlatCollection(templates);
      const templateChildIds = new Set(
        templateGroups.flatMap((template) => template.group.areaIds),
      );

      const nonTemplateHighways = highwayAreas.filter(
        (highway) => !templateChildIds.has(highway.id),
      );
      const vehiclesToAdd = newEnabledIds.filter((id) => !enabledVehicleIds.includes(id));
      const hasShuttleVehicle = vehiclesToAdd.some(isShuttleVehicleId);
      let addNewVehiclesToHighways = false;

      if (nonTemplateHighways.length > 0 && vehiclesToAdd.length > 0 && !hasShuttleVehicle) {
        addNewVehiclesToHighways = await confirm({
          title: t('interface:setup.floorPlan.vehicle_types.add_new_vehicles_to_all_highways'),
          message: vehiclesToAdd.join(', '),
        });
      }

      appSvc.showLoadContainer('TRANSPARENT');

      try {
        const {
          metaData: { projectId, alternativeId, revisionId },
        } = useFloorplanDesignStore.getState();

        await vehicleSvc.reconcileEnabledVehicleTypes(
          newEnabledIds,
          projectId,
          alternativeId,
          revisionId,
          addNewVehiclesToHighways,
        );
      } finally {
        appSvc.hideLoadContainer();
      }

      doesVehiclesHaveLoadType(newEnabledIds, loadTypesInStore.map((lt) => lt.id));
    },
    [appSvc, confirm, enabledVehicleIds, t, vehicleSvc, loadTypesInStore, doesVehiclesHaveLoadType],
  );

  const onDeleteClick = useCallback(
    async (deleteId: string) => {
      if (!isLatest) {
        return;
      }

      const isLastEnabledVehicle = enabledVehicleIds.length <= 1;
      const isLastRegularVehicle = !isShuttleVehicleId(deleteId) && enabledRegularVehicleCount <= 1;

      if (isLastEnabledVehicle || isLastRegularVehicle) {
        snackbarSvc.showMessage(
          t(
            'interface:setup.floorPlan.vehicle_types_select_dialog.warnings.at_least_one_regular_vehicle',
            'At least one regular vehicle must remain enabled.',
          ),
        );
        return;
      }

      const vehicleType = getVehicleTypeById(deleteId);

      if (!vehicleType) {
        snackbarSvc.showMessage(
          t('interface:setup.floorPlan.vehicle_types_select_dialog.warnings.vehicle_not_found'),
        );
        return;
      }

      const canDeleteVehicle = await canRemoveVehicle(deleteId);

      if (!canDeleteVehicle) {
        return;
      }

      appSvc.showLoadContainer('TRANSPARENT');

      try {
        const {
          metaData: { projectId, alternativeId, revisionId },
        } = useFloorplanDesignStore.getState();

        await vehicleSvc.reconcileEnabledVehicleTypes(
          enabledVehicleIds.filter((vehicleId) => vehicleId !== deleteId),
          projectId,
          alternativeId,
          revisionId,
        );
      } finally {
        appSvc.hideLoadContainer();
      }
    },
    [isLatest, enabledVehicleIds, enabledRegularVehicleCount, getVehicleTypeById, canRemoveVehicle, appSvc, t, snackbarSvc, vehicleSvc],
  );

  return (
    <>
      {assetsVehicleId && (
        <AssetPanel anchor={panelRef} vehicleId={assetsVehicleId} close={handleCloseAssetPanel} />
      )}

      <ThemeProvider theme={setupPropertyPanelTheme}>
        <Stack spacing={1} sx={{ px: 2 }} ref={panelRef}>
          <Stack direction='row' justifyContent='space-between' alignItems='center'>
            <Typography
              align='center'
              sx={{
                fontSize: '14px',
                fontWeight: 400,
              }}
            >
              {t('setup.floorPlan.vehicle_types.vehicles_title', 'Vehicle models', {
                count: enabledVehicleIds.length,
              })}
            </Typography>

            <StyledTooltip title={t('setup.floorPlan.vehicle_types.add_button.title')}>
              <span>
                <IconButton
                  disabled={!isLatest}
                  onClick={handleOpenDialog}
                  sx={{ padding: 0, border: 'none' }}
                >
                  <AddIcon data-testid='add-icon' />
                </IconButton>
              </span>
            </StyledTooltip>
          </Stack>

          <Stack gap={1}>
            {enabledVehicles.map((vehicle) => (
              <FloorPlanEnabledVehicleItem
                key={vehicle.id}
                name={vehicle.name}
                vehicleId={vehicle.id}
                imageUrl={vehicle.image.url}
                deletable={isVehicleDeletable(vehicle.id)}
                disabled={!isLatest}
                deleteBtnTooltipText={t('setup.floorPlan.vehicle_types.delete_button.title')}
                onDeleteClick={onDeleteClick}
                assetsVehicleId={assetsVehicleId}
                onFileInteractionClick={setAssetsVehicleId}
              />
            ))}
          </Stack>
        </Stack>
      </ThemeProvider>

      <VehicleTypeSelectDialog
        open={open}
        selectedVehiclesIds={enabledVehicleIds}
        onSelect={onSelect}
        onClose={handleCloseDialog}
      />

      <ConfirmDialog negativeBtnTextCustom={t('common:no')} />
    </>
  );
};

export const VehicleTypes = memo(VehicleTypesComponent);
